import { Field } from '@/components/Fields';
import { EditIcon } from '@/components/icons/Icons';
import { useAppDispatch, useErrorIndicator } from '@/hooks';
import { DocumentId } from '@/types';
import {
  Button,
  FormControl,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { updateDocumentId } from './actions';

interface DocumentIdFormProps {
  docIdData: DocumentId;
}

export const DocumentIdForm: React.FC<DocumentIdFormProps> = ({ docIdData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { showError } = useErrorIndicator();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    dispatch(
      updateDocumentId({
        id: docIdData.id,
        enabled: values.enabled,
        docId: values.newDocId,
      }),
    )
      .unwrap()
      .then(() => {
        setIsLoading(false);
        toast({
          title: 'Successfully updated Document ID',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onClose();
      })
      .catch(() => {
        setIsLoading(false);
        showError('Error updating Document ID');
      });
  };

  const UpdateDocIdSchema = Yup.object().shape({
    newDocId: Yup.number().required('Required'),
    enabled: Yup.string().required('Required'),
  });

  return (
    <>
      <EditIcon fill="white" onClick={onOpen} cursor="pointer" _hover={{ fill: 'gray.600' }} />
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent px={3} py={3} minW={{ base: 'unset', md: 'fit-content' }}>
          <ModalHeader>
            <HStack gap={8}>
              <Text>Update Document Data</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Formik
              key={docIdData.id}
              initialValues={{
                newDocId: docIdData.docId,
                enabled: docIdData.enabled,
              }}
              onSubmit={handleSubmit}
              validationSchema={UpdateDocIdSchema}
            >
              {({ handleSubmit, isSubmitting, isValid, dirty, errors, touched }) => (
                <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <FormControl>
                    {docIdData.docId !== -1 && (
                      <Field
                        type="number"
                        bg="white"
                        border="2px solid"
                        borderColor="gray.600"
                        placeholderFontSize="sm"
                        placeholderFontWeight="400"
                        placeholder="New Document ID"
                        name="newDocId"
                        errors={errors.newDocId}
                        touched={touched.newDocId}
                        fontSize="sm"
                        size="xs"
                        mb={6}
                        labelMb="8px"
                        label="Document ID"
                      />
                    )}
                    <Field
                      type="select"
                      options={[
                        { value: 'false', label: 'FALSE' },
                        { value: 'true', label: 'TRUE' },
                      ]}
                      bg="white"
                      border="2px solid"
                      borderColor="gray.600"
                      placeholderFontSize="sm"
                      placeholderFontWeight="400"
                      name="enabled"
                      errors={errors.enabled}
                      touched={touched.enabled}
                      fontSize="sm"
                      size="xs"
                      mb={2}
                      labelMb="8px"
                      label="Enabled"
                    />
                    <Button
                      fontWeight="semibold"
                      variant="primary"
                      mt={4}
                      maxW="160px"
                      height="45px"
                      float="right"
                      w="full"
                      type="submit"
                      isDisabled={!isValid || !dirty || isSubmitting || isLoading}
                      isLoading={isSubmitting || isLoading}
                    >
                      Save
                    </Button>
                  </FormControl>
                </Form>
              )}
            </Formik>
          </ModalBody>
          <ModalFooter>
            <VStack>
              <Text color="red" fontWeight="bold">
                Updating Document Data will change document values for all application users.
              </Text>
              <Text color="red" fontWeight="bold">
                Use with caution!
              </Text>
            </VStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
